// -----------------------------------------------------------------------------
// JWT payload reader (no signature verification — the backend verifies).
//
// We only decode the payload to read tenant_id / user_id / license_type / scopes
// and expiration. Signature trust comes from HTTPS + the API verifying the JWT
// on every request.
// -----------------------------------------------------------------------------

import type { JwtPayload } from '../api/types';

/** Decodes a base64url string (RFC 7515) into a UTF-8 string. */
function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const binary = atob(padded + padding);

  // Convert binary string to UTF-8.
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Decodes a JWT's payload. Returns null when the token is malformed,
 * lacks the expected claims, or the scopes claim cannot be parsed.
 */
export function decodeJwtPayload(jwt: string): JwtPayload | null {
  const parts = jwt.split('.');
  if (parts.length !== 3) {
    return null;
  }
  const payloadSegment = parts[1];
  if (payloadSegment === undefined || payloadSegment === '') {
    return null;
  }

  let rawPayload: unknown;
  try {
    rawPayload = JSON.parse(base64UrlDecode(payloadSegment));
  } catch {
    return null;
  }

  if (typeof rawPayload !== 'object' || rawPayload === null) {
    return null;
  }
  const obj = rawPayload as Record<string, unknown>;

  const tenantId = obj['tenant_id'];
  const userId = obj['user_id'];
  const licenseType = obj['license_type'];
  const exp = obj['exp'];
  const rawScopes = obj['scopes'];

  if (
    typeof tenantId !== 'string' ||
    typeof userId !== 'string' ||
    typeof licenseType !== 'string' ||
    typeof exp !== 'number'
  ) {
    return null;
  }

  const scopes = parseScopesClaim(rawScopes);
  if (scopes === null) {
    return null;
  }

  return {
    tenant_id: tenantId,
    user_id: userId,
    license_type: licenseType as JwtPayload['license_type'],
    scopes,
    exp,
    ...(typeof obj['iat'] === 'number' ? { iat: obj['iat'] as number } : {}),
    ...(typeof obj['sub'] === 'string' ? { sub: obj['sub'] as string } : {}),
    ...(typeof obj['jti'] === 'string' ? { jti: obj['jti'] as string } : {}),
  };
}

/**
 * The backend serializes the `scopes` claim as a JSON-encoded string (it uses
 * `JsonClaimValueTypes.JsonArray`, but System.IdentityModel still emits the
 * value as a quoted JSON string). We accept either form to be defensive.
 */
function parseScopesClaim(raw: unknown): string[] | null {
  if (Array.isArray(raw)) {
    return raw.every((item) => typeof item === 'string') ? (raw as string[]) : null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
        return parsed as string[];
      }
    } catch {
      // Fallback: comma-separated value (defensive, not currently emitted).
      const list = raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
      return list.length > 0 ? list : null;
    }
  }
  return null;
}
